import React, { useEffect, useState } from 'react';

// Styling constants
const CONTAINER_CLASSES = "fixed bottom-4 right-4 left-4 md:left-auto z-50";
const BANNER_CLASSES = "bg-white w-full md:max-w-md rounded-xl shadow-2xl ml-auto";
const BUTTON_PRIMARY_CLASSES = "bg-black text-white px-6 py-3 rounded text-sm";
const BUTTON_OUTLINE_CLASSES = "bg-white border border-black text-black px-6 py-3 rounded text-sm";
const BUTTON_SECONDARY_CLASSES = "text-black font-medium text-sm hover:underline";
const TOGGLE_BUTTON_CLASSES = "text-xs mr-2 text-gray-400 font-bold cursor-pointer w-4";

// GTM ID constant
const GTM_ID = 'GTM-KC5G5WLD';
const GA4_ID = 'G-YNTH5CMJJS';

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

// Consent Manager Component
export function ConsentManager() {
  const [consentShown, setConsentShown] = useState(false);
  const [debugVisible, setDebugVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consentState, setConsentState] = useState({
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  });
  const [gtmReady, setGtmReady] = useState(false);
  
  // Settings preferences state
  const [settingsViewPreferences, setSettingsViewPreferences] = useState({
    analytics: false,
    advertising: false
  });
  
  // Expanded sections for cookie settings
  const [expandedSections, setExpandedSections] = useState({
    essential: false,
    analytics: false,
    advertising: false
  });

  // GTM ready handler
  const handleGtmReady = () => {
    console.log('🚀 GTM is now ready');
    setGtmReady(true);
  };

  // Initialization on first load
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Function to check GTM readiness
    const checkGTMReady = () => {
      if (window.google_tag_manager && window.google_tag_manager[GTM_ID]) {
        handleGtmReady();
        return true;
      }
      return false;
    };
    
    // Check immediately
    if (!checkGTMReady()) {
      // If GTM not ready, set interval to check
      const gtmCheckInterval = setInterval(() => {
        if (checkGTMReady()) {
          clearInterval(gtmCheckInterval);
        }
      }, 500);
      
      // Clear interval on unmount
      return () => clearInterval(gtmCheckInterval);
    }
  }, []);

  // IMPORTANT: Block analytics until consent is received
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Default block all GA4 tracks
    window[`ga-disable-${GA4_ID}`] = true;
    console.log('🔒 GA4 tracking disabled by default on component mount');
  }, []);

  // Handling consents and showing banner after determining GTM state
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    console.log('🔒 Checking consent settings...');
    
    // Check if this is a strict privacy region (GDPR/CCPA)
    const isStrictRegion = window.isStrictPrivacyRegion ?? true; // Default strict
    console.log('🌍 Geographic privacy requirement:', isStrictRegion ? 'STRICT (GDPR/CCPA)' : 'RELAXED');
    
    // Check saved settings
    const consentCookie = localStorage.getItem('consent_preferences');
    if (consentCookie) {
      try {
        const savedConsent = JSON.parse(consentCookie);
        console.log('🔄 Found saved consent settings:', savedConsent);
        
        // If settings exist - don't show banner
        setConsentShown(false);
        
        // Initialize settings state based on saved data
        setSettingsViewPreferences({
          analytics: savedConsent.analytics || false,
          advertising: savedConsent.advertising || false
        });
        
        // IMPORTANT: Immediately update GA4 flag based on saved settings
        window[`ga-disable-${GA4_ID}`] = !savedConsent.analytics;
        console.log(`📊 GA4 tracking ${savedConsent.analytics ? 'enabled' : 'still DISABLED'} based on saved preferences`);
        
        // If GTM is ready - apply settings
        if (gtmReady) {
          console.log('🔄 Applying saved consent settings to GTM');
          updateConsentOnly(savedConsent.analytics, savedConsent.advertising);
        }
      } catch (e) {
        console.error('❌ Error reading saved consent settings:', e);
        setConsentShown(isStrictRegion); // Only show if strict region
      }
    } else {
      // No saved settings - ALWAYS show banner for user choice
      console.log('ℹ️ No saved settings: showing consent banner');
      setConsentShown(true);
      
      // Set default preferences based on region
      if (isStrictRegion) {
        console.log('🌍 Strict region (GDPR/CCPA): defaults to DENIED');
        setSettingsViewPreferences({
          analytics: false, // Denied by default
          advertising: false // Denied by default
        });
        // Keep everything blocked until user consent
      } else {
        console.log('🌍 Relaxed region: MINIMUM analytics enabled, ads denied');
        setSettingsViewPreferences({
          analytics: true,  // Granted by default
          advertising: false // Still denied by default
        });
        // Immediately enable minimum analytics in relaxed regions
        updateConsentOnly(true, false);
      }
    }
    
    // Setup debugger if GTM is ready and in development mode
    if (gtmReady && isDevelopment) {
      setupConsentDebugger();
    }
  }, [gtmReady]); // Run when GTM readiness state changes
  
  // Setup consent debugger
  const setupConsentDebugger = () => {
    if (!window.google_tag_manager) {
      console.warn('⚠️ GTM not initialized properly for debugger');
      return;
    }
    
    const checkConsentState = () => {
      try {
        // Important: correct access to consent object in dataLayer
        if (window.google_tag_manager[GTM_ID] && 
            window.google_tag_manager[GTM_ID].dataLayer && 
            window.google_tag_manager[GTM_ID].dataLayer.get) {
          
          const currentState = window.google_tag_manager[GTM_ID].dataLayer.get('consent') || {};
          console.log('🔍 Current consent state:', currentState);
          setConsentState(currentState);
          return currentState;
        } else {
          console.warn('⚠️ Cannot access GTM dataLayer properly');
          return {};
        }
      } catch (e) {
        console.error('❌ Error getting consent state:', e);
        return {};
      }
    };
    
    // Check state immediately
    checkConsentState();
    
    // Monitor events in dataLayer
    if (window.dataLayer) {
      const originalPush = window.dataLayer.push;
      window.dataLayer.push = function() {
        // First call original method
        const result = originalPush.apply(this, arguments);
        
        // Check if this was a consent-related event
        try {
          const args = arguments[0];
          if (args && args.event && 
              (args.event === 'consent_updated' || 
               args.event === 'consent_default' || 
               args.event.includes('consent') ||
               args.event === 'gtm.js')) {
            console.log('📊 DataLayer event detected:', args.event, args);
            setTimeout(checkConsentState, 500);
          }
        } catch (err) {
          console.error('❌ Error processing dataLayer event:', err);
        }
        
        return result;
      };
    }
  };
  
  // Function only for updating GTM state without saving and UI
  const updateConsentOnly = (analytics, advertising) => {
    // Update consent directly in dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'consent_update',
        'consent': {
          'analytics_storage': analytics ? 'granted' : 'denied',
          'ad_storage': advertising ? 'granted' : 'denied',
          'ad_user_data': advertising ? 'granted' : 'denied',
          'ad_personalization': advertising ? 'granted' : 'denied'
        }
      });
      console.log('✅ Consent settings pushed to dataLayer');
    }
    
    // IMPORTANT: Force disable GA4 if analytics not allowed
    if (typeof window !== 'undefined') {
      window[`ga-disable-${GA4_ID}`] = !analytics;
      console.log(`✅ GA4 tracking ${analytics ? 'enabled' : 'DISABLED'} via ga-disable flag`);
    }
    
    // Update via gtag API if available
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': analytics ? 'granted' : 'denied',
        'ad_storage': advertising ? 'granted' : 'denied',
        'ad_user_data': advertising ? 'granted' : 'denied',
        'ad_personalization': advertising ? 'granted' : 'denied'
      });
      console.log('✅ Consent settings updated via gtag() API');
      
      // Update consent mode settings
      window.gtag('set', 'consent', {
        'analytics_storage': analytics ? 'granted' : 'denied',
        'ad_storage': advertising ? 'granted' : 'denied',
        'ad_user_data': advertising ? 'granted' : 'denied',
        'ad_personalization': advertising ? 'granted' : 'denied'
      });
    }
    
    // Send explicit event to dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'user_consent_updated',
        'analytics_consent': analytics,
        'advertising_consent': advertising
      });
      console.log('✅ User consent event pushed to dataLayer');
    }
  };
  
  // Full consent update function
  const updateConsent = (analytics, advertising) => {
    console.log('🔄 updateConsent called:', { analytics, advertising, gtmReady });
    
    // IMPORTANT: Immediately update GA4 flag
    if (typeof window !== 'undefined') {
      window[`ga-disable-${GA4_ID}`] = !analytics;
      console.log(`📊 GA4 tracking ${analytics ? 'enabled' : 'still DISABLED'} (immediate update)`);
    }
    
    // Save choice to localStorage
    localStorage.setItem('consent_preferences', JSON.stringify({
      analytics, 
      advertising,
      timestamp: new Date().toISOString()
    }));
    console.log('✅ Settings saved to localStorage');
    
    // Hide banner
    setConsentShown(false);
    setShowSettings(false);
    
    // Apply to GTM if it's ready
    if (window.dataLayer) {
      // First, update default consent
      window.dataLayer.push({
        'event': 'consent_default',
        'consent': {
          'analytics_storage': analytics ? 'granted' : 'denied',
          'ad_storage': advertising ? 'granted' : 'denied',
          'ad_user_data': advertising ? 'granted' : 'denied',
          'ad_personalization': advertising ? 'granted' : 'denied'
        }
      });
      
      // Then, update consent explicitly
      window.dataLayer.push({
        'event': 'consent_update',
        'consent': {
          'analytics_storage': analytics ? 'granted' : 'denied',
          'ad_storage': advertising ? 'granted' : 'denied',
          'ad_user_data': advertising ? 'granted' : 'denied',
          'ad_personalization': advertising ? 'granted' : 'denied'
        }
      });
      
      // Send additional user consent event
      window.dataLayer.push({
        'event': 'user_consent_updated',
        'analytics_consent': analytics,
        'advertising_consent': advertising
      });
      
      console.log('✅ Multiple consent events pushed to dataLayer');
    }
    
    // If gtag is available, update directly
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': analytics ? 'granted' : 'denied',
        'ad_storage': advertising ? 'granted' : 'denied',
        'ad_user_data': advertising ? 'granted' : 'denied',
        'ad_personalization': advertising ? 'granted' : 'denied'
      });
      console.log('✅ Consent settings updated via gtag');
    }
    
    // Check state after update
    setTimeout(() => {
      try {
        if (window.google_tag_manager && 
            window.google_tag_manager[GTM_ID] && 
            window.google_tag_manager[GTM_ID].dataLayer) {
          
          const updatedState = window.google_tag_manager[GTM_ID].dataLayer.get('consent') || {};
          setConsentState(updatedState);
        }
      } catch (e) {
        console.error('❌ Error checking updated consent state:', e);
      }
    }, 1000);
  };
  
  const getStatusLabel = (status) => {
    return status === 'granted' ? '✅ Allowed' : '❌ Denied';
  };
  
  const handleAcceptAll = () => updateConsent(true, true);
  const handleRejectAll = () => updateConsent(false, false);
  
  // Get region-aware default text
  const getRegionText = () => {
    const isStrictRegion = window.isStrictPrivacyRegion ?? true;
    if (isStrictRegion) {
      return {
        title: "Cookies managing",
        description: "We use cookies to provide the best site experience. Choose your preferences below."
      };
    } else {
      return {
        title: "Cookies managing",
        description: "We use cookies to provide the best site experience. Analytics cookies are already enabled to help us improve our service. You can accept additional cookies or manage your preferences."
      };
    }
  };
  
  // Toggle section in settings view
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Function to toggle individual cookie settings
  const handleToggleCookie = (type) => {
    setSettingsViewPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };
  
  // Apply selected settings
  const handleConfirmSettings = () => {
    updateConsent(settingsViewPreferences.analytics, settingsViewPreferences.advertising);
  };
  
  // Toggle switch styling
  const getToggleSwitchClasses = (enabled) => ({
    container: `w-10 h-5 rounded-full relative ${enabled ? 'bg-black' : 'bg-gray-300'} cursor-pointer`,
    slider: `absolute w-4 h-4 rounded-full bg-white top-0.5 transition-all ${enabled ? 'right-0.5' : 'left-0.5'}`
  });
  
  // Cookie section component for settings view
  const renderCookieSection = (type, label, description) => {
    const isExpanded = expandedSections[type];
    const isEnabled = settingsViewPreferences[type];
    const toggleClasses = getToggleSwitchClasses(isEnabled);
    const isEssential = type === 'essential';
    
    return (
      <div className="py-4">
        <div className="flex justify-between items-center">
          <div 
            className="flex items-center cursor-pointer" 
            onClick={() => toggleSection(type)}
          >
            <button className={TOGGLE_BUTTON_CLASSES}>
              {isExpanded ? '−' : '+'}
            </button>
            <span className="pl-2 text-sm">{label}</span>
          </div>
          
          <div className="flex items-center">
            {isEssential ? (
              <span className="text-blue-500 font-bold text-sm">Always Enabled</span>
            ) : (
              <>
                <span className={`mr-2 text-sm ${isEnabled ? 'text-black' : 'text-gray-400'}`}>
                  {isEnabled ? 'Enabled' : 'Disabled'}
                </span>
                <div 
                  onClick={() => handleToggleCookie(type)}
                  className={toggleClasses.container}
                >
                  <div 
                    className={toggleClasses.slider}
                    style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                  ></div>
                </div>
              </>
            )}
          </div>
        </div>
        
        {isExpanded && (
          <div className="mt-2 text-gray-600 pl-8 text-sm">
            {description}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Consent banner with improved design - shown only when needed */}
      {consentShown && !showSettings && (
        <div className={CONTAINER_CLASSES}>
          <div className={BANNER_CLASSES}>
            <div className="p-6">
              <h2 className="text-lg font-bold mb-1">{getRegionText().title}</h2>
              <p className="mb-6 text-sm">
                {getRegionText().description}
              </p>
              <div className="flex gap-4">
                <button 
                  onClick={handleAcceptAll}
                  className={BUTTON_PRIMARY_CLASSES}
                >
                  Accept All
                </button>
                <button 
                  onClick={handleRejectAll}
                  className={BUTTON_OUTLINE_CLASSES}
                >
                  Reject All
                </button>
                <button 
                  onClick={() => setShowSettings(true)}
                  className={BUTTON_SECONDARY_CLASSES}
                >
                  Cookie Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Settings view */}
      {consentShown && showSettings && (
        <div className={CONTAINER_CLASSES}>
          <div className={BANNER_CLASSES}>
            {/* Header with back arrow */}
            <div className="flex items-center p-4 pl-6 mb-0">
              <button 
                onClick={() => setShowSettings(false)}
                className="text-gray-500 mr-2"
              >
                ←
              </button>
              <span className="text-xs text-gray-500">Cookies managing</span>
            </div>
            
            {/* Settings content */}
            <div className="px-6 pt-1 pb-6">
              <h2 className="text-lg font-bold mb-4">Cookie Settings</h2>
              <p className="mb-2 text-sm">
                Cookies necessary for the correct operation of the site are always enabled.
                Other cookies are configurable.
              </p>
              
              <div className="space-y-4">
                {renderCookieSection(
                  'essential', 
                  'Essential cookies', 
                  'These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms.'
                )}
                
                {renderCookieSection(
                  'analytics', 
                  'Analytics cookies', 
                  'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. They allow us to measure and improve the performance of our site.'
                )}
                
                {renderCookieSection(
                  'advertising', 
                  'Advertising cookies', 
                  'These cookies are used to track visitors across websites. They are set to display targeted advertisements based on your interests and online behavior. They also help measure the effectiveness of advertising campaigns.'
                )}
              </div>
              
              {/* Confirm button */}
              <div className="mt-8">
                <button 
                  onClick={handleConfirmSettings}
                  className={BUTTON_PRIMARY_CLASSES}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Debug button - ONLY shown in development */}
      {isDevelopment && (
        <div className="fixed top-2 right-2 z-40">
          <button 
            onClick={() => setDebugVisible(!debugVisible)}
            className="bg-gray-800 text-white px-3 py-1 rounded text-xs"
          >
            🔍 Debug
          </button>
        </div>
      )}
      
      {/* Debug panel - ONLY shown in development */}
      {isDevelopment && debugVisible && (
        <div className="fixed top-10 right-2 w-80 bg-white shadow-xl rounded-lg p-3 z-40 border border-gray-200 text-xs">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-bold">Consent Mode Debug</h3>
            <button 
              onClick={() => setDebugVisible(false)}
              className="text-gray-600 hover:text-gray-800"
            >
              ✕
            </button>
          </div>
          
          <div className="bg-gray-100 p-2 rounded mb-2">
            <h4 className="font-bold mb-1">Current settings:</h4>
            <div>
              {Object.entries(consentState).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span>{key}:</span>
                  <span className={value === 'granted' ? 'text-green-600' : 'text-red-600'}>
                    {getStatusLabel(value)}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-2 text-xs">
              <strong>GTM Ready:</strong> {gtmReady ? '✅ Yes' : '❌ No'}
            </div>
            <div className="mt-2 text-xs">
              <strong>GA4 Disabled:</strong> {typeof window !== 'undefined' && window[`ga-disable-${GA4_ID}`] ? '✅ Yes' : '❌ No'}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-1 mb-2">
            <button 
              onClick={() => updateConsent(true, true)}
              className="bg-green-600 text-white px-2 py-1 rounded text-xs"
            >
              Accept All
            </button>
            <button 
              onClick={() => updateConsent(false, false)}
              className="bg-red-600 text-white px-2 py-1 rounded text-xs"
            >
              Reject All
            </button>
            <button 
              onClick={() => updateConsent(true, false)}
              className="bg-yellow-600 text-white px-2 py-1 rounded text-xs"
            >
              Analytics Only
            </button>
            <button 
              onClick={() => updateConsent(false, true)}
              className="bg-yellow-600 text-white px-2 py-1 rounded text-xs"
            >
              Ads Only
            </button>
          </div>
          
          <div className="text-xs text-gray-600">
            <p>Check in Network (F12):</p>
            <ul className="list-disc pl-4">
              <li>Requests collect? / analytics.js / gtm.js</li>
              <li>gcs: 0=denied, 1=granted</li>
            </ul>
          </div>
          
          <button 
            onClick={() => {
              localStorage.removeItem('consent_preferences');
              console.log('🗑️ Consent settings reset');
              location.reload();
            }}
            className="w-full mt-2 bg-gray-600 text-white px-2 py-1 rounded text-xs"
          >
            Reset and reload
          </button>
          
          <button 
            onClick={() => {
              console.log('📊 Manual event test');
              if (window.dataLayer) {
                window.dataLayer.push({
                  'event': 'user_consent_updated',
                  'analytics_consent': true,
                  'advertising_consent': false
                });
                
                // Force update consent
                updateConsentOnly(true, false);
              }
            }}
            className="w-full mt-2 bg-blue-600 text-white px-2 py-1 rounded text-xs"
          >
            Test Event
          </button>
        </div>
      )}
    </>
  );
}

// GTM initialization scripts
export const gtmDataLayerScript = `
  // IMPORTANT: By default block GA4 until consent is received
  window['ga-disable-${GA4_ID}'] = true;
  console.log('🔒 GA4 tracking disabled by default until consent is given');
  
  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  window.gtag = gtag;
  
  // Set default values for consents
  window.dataLayer.push({
    'event': 'consent_default',
    'consent': {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied',
      'functionality_storage': 'granted',
      'security_storage': 'granted',
      'personalization_storage': 'denied',
      'wait_for_update': 500
    }
  });
  
  // Additional Consent Mode settings
  gtag('set', 'url_passthrough', true);
  gtag('set', 'ads_data_redaction', true);
  
  console.log('✅ Consent Mode initialized with default parameters');
`;

// Google Tag Manager initialization script
export const gtmScript = `
  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${GTM_ID}');
  
  console.log('✅ Google Tag Manager loading initiated');
`;

// GTM noscript tag content (for SSR)
export const gtmNoScript = `
  <iframe 
    src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}"
    height="0" 
    width="0" 
    style="display:none;visibility:hidden"
  ></iframe> 
`;